import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { InfoHint } from "@/components/ui/InfoHint";
import { cn } from "@/lib/cn";

/**
 * A rate against a target, with where it was last period.
 *
 * ── Why this is not a `Stat` ───────────────────────────────────────────────
 *
 * `Stat` is a count. "41 visits" is complete on its own. A compliance rate is
 * not: 94% is excellent against a 90% target and a problem against 100%, and it
 * is either good news or bad news depending on whether last month was 89% or
 * 97%. All three numbers have to be on the tile or the tile is decoration.
 *
 * ── Why the bar is accent and never amber ──────────────────────────────────
 *
 * Colour in this product means severity, and this dashboard puts compliance
 * tiles a scroll away from a chart of genuine Caution and Stop findings. A meter
 * that turned amber when it slipped below target would be the fourth amber thing
 * on the page and the only one that is not a hazard.
 *
 * ── The track carries the figure, and nothing else ─────────────────────────
 *
 * There was a `target` tick on the track: a 2px hairline at the target's
 * position. It is gone from both clients. A hairline is not a number - nothing
 * on it said what the target *was*, so a reader either already knew, in which
 * case the tick told them nothing, or could not tell whether the bar was two
 * points short of it or twenty. The delta beside the figure is the comparison
 * that survives, because it has an arrow, a word and a magnitude.
 *
 * `target` is still a prop: it sets the seconds meters' ceiling, which is what
 * makes a 45s bar and a 96% bar mean comparable things.
 *
 * ── `goal` ─────────────────────────────────────────────────────────────────
 *
 * Two of the four meters this renders are the wrong way up. Coverage should sit
 * at or above its target; capture time should sit at or below the 90-second
 * budget. A component that assumed "higher is better" would draw the fastest
 * site as the failing one, which is the kind of bug nobody reports because the
 * chart looks confident.
 */
export function MeterStat({
  label,
  value,
  previous,
  target,
  unit,
  goal,
  neutral = false,
  note,
  className,
}: {
  label: string;
  /**
   * Null is "nothing to measure", which is a different fact from zero: a site
   * with no visits this month has no coverage rate, and 0% there would read as
   * total non-compliance rather than as a quiet month.
   */
  value: number | null;
  /** The same figure over the preceding period of equal length. 0 when that period was empty. */
  previous: number;
  target: number;
  unit: "percent" | "seconds";
  goal: "atLeast" | "atMost";
  /**
   * Stop-work rate and assessment time: a move is not good or bad news by
   * itself (fewer stops can mean nobody is looking; a faster check can mean a
   * rushed one), so the delta stays truthful about direction without judging it.
   */
  neutral?: boolean;
  /** What the figure means, via a hover/focus tooltip - every meter carries one, unlike `Stat`. */
  note: string;
  className?: string;
}) {
  // The delta is a RELATIVE percentage, not the raw difference in the figure's
  // own unit.
  //
  // These four tiles sit in one row and used to speak two languages: coverage
  // moved "4 pts" and assessment time moved "8s", so a reader comparing them
  // was comparing a percentage-point difference against a duration and had to
  // hold both scales at once. A percentage answers the question the row is
  // actually scanned for - "which of these moved most" - in one unit.
  //
  // A ratio against a zero preceding period is mathematically undefined, but
  // the tile still owes a number: **a first reading counts as a full 100%
  // move**, in whichever direction `direction` already points. Coverage that
  // did not exist last period and reads 100% this one is not a number a reader
  // can act on if the tile just says "New" - "up 100%" is.
  const direction = value === null ? 0 : Math.sign(round(value - previous));
  const magnitude =
    value === null
      ? null
      : previous !== 0
        ? Math.abs(round(((value - previous) / previous) * 100))
        : 100;

  // Better means "moved towards the target", which for assessment time is down.
  const better = goal === "atLeast" ? direction > 0 : direction < 0;

  // Percent meters fill against 100 so the bar means the same thing on every
  // tile. A seconds meter has no natural ceiling, so it fills against twice the
  // budget, which puts the target at the midpoint.
  //
  // **There is no target tick on the track any more.** A 2px hairline is not a
  // number: nothing on it says what the target *is*, so a reader either already
  // knew (and did not need the tick) or could not tell whether the bar was two
  // points short or twenty. `meeting` carries the same fact as a badge with a
  // word in it, which is the rule the fill colour already follows.
  const ceiling = unit === "percent" ? 100 : target * 2;
  const fill = value === null ? 0 : clampPercent((value / ceiling) * 100);

  // Neutral meters still point where the figure actually moved - only the
  // colour drops the "up is good" judgment `better` makes for the other two.
  const ChangeIcon =
    direction === 0 ? Minus : (neutral ? direction > 0 : better) ? ArrowUpRight : ArrowDownRight;

  return (
    <div className={cn("meter", className)}>
      <span className="info-hint-row">
        <p className="meter__label">{label}</p>
        <InfoHint text={note} />
      </span>

      <div className="meter__figure">
        <span className="meter__value">{value === null ? "-" : formatValue(value, unit)}</span>
        <span className={cn("meter__change", changeTone(direction, better, neutral))}>
          <ChangeIcon aria-hidden />
          {changeLabel(direction, magnitude)}
        </span>
      </div>

      <div className="meter__track">
        {/* Computed per tile, which is what `style` stays for. */}
        <span className="meter__fill" style={{ width: `${fill}%` }} />
      </div>
    </div>
  );
}

function formatValue(value: number, unit: "percent" | "seconds"): string {
  return unit === "percent" ? `${round(value)}%` : `${Math.round(value)}s`;
}

function changeLabel(direction: number, magnitude: number | null): string {
  if (direction === 0) return "No change";
  return `${magnitude ?? 100}%`;
}

function changeTone(direction: number, better: boolean, neutral: boolean): string {
  if (direction === 0 || neutral) return "meter__change--flat";
  return better ? "meter__change--better" : "meter__change--worse";
}

const round = (value: number) => Math.round(value * 10) / 10;
const clampPercent = (value: number) => Math.min(100, Math.max(0, value));
